import React from 'react';

interface MovaAstroLogoProps {
  className?: string;
  showText?: boolean;
  variant?: 'horizontal' | 'stacked';
}

export function MovaAstroLogo({ className = "", showText = true, variant = 'horizontal' }: MovaAstroLogoProps) {
  if (!showText) {
    return (
      <div className={`relative w-14 h-14 ${className}`}>
        <svg viewBox="0 0 120 120" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="yantraIcon" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#DC2626" />
              <stop offset="50%" stopColor="#F97316" />
              <stop offset="100%" stopColor="#FBBF24" />
            </linearGradient>
          </defs>
          <circle cx="60" cy="60" r="54" fill="none" stroke="#D1D5DB" strokeWidth="6" />
          <circle cx="60" cy="60" r="48" fill="none" stroke="url(#yantraIcon)" strokeWidth="4" opacity="0.3" />
          
          {/* Interlocking triangles - Sri Yantra */}
          <path d="M 60 25 L 85 70 L 35 70 Z" fill="none" stroke="url(#yantraIcon)" strokeWidth="6" opacity="0.7" />
          <path d="M 60 95 L 35 50 L 85 50 Z" fill="none" stroke="url(#yantraIcon)" strokeWidth="6" opacity="0.7" />
          <path d="M 60 35 L 78 65 L 42 65 Z" fill="none" stroke="url(#yantraIcon)" strokeWidth="6" opacity="0.6" />
          <path d="M 60 85 L 42 55 L 78 55 Z" fill="none" stroke="url(#yantraIcon)" strokeWidth="6" opacity="0.6" />
          
          <circle cx="60" cy="60" r="15" fill="url(#yantraIcon)" opacity="0.2" />
          <circle cx="60" cy="60" r="8" fill="white" stroke="#DC2626" strokeWidth="6" />
          <circle cx="60" cy="60" r="3" fill="url(#yantraIcon)" />
        </svg>
      </div>
    );
  }

  if (variant === 'stacked') {
    return (
      <div className={`flex flex-col items-center ${className}`}>
        {/* Logo Icon */}
        <div className="relative w-20 h-20 mb-4">
          <svg viewBox="0 0 120 120" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="yantraStacked" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#DC2626" />
                <stop offset="50%" stopColor="#F97316" />
                <stop offset="100%" stopColor="#FBBF24" />
              </linearGradient>
            </defs>
            <circle cx="60" cy="60" r="54" fill="none" stroke="#D1D5DB" strokeWidth="6" />
            <circle cx="60" cy="60" r="48" fill="none" stroke="url(#yantraStacked)" strokeWidth="4" opacity="0.3" />
            
            {/* Interlocking triangles - Sri Yantra */}
            <path d="M 60 25 L 85 70 L 35 70 Z" fill="none" stroke="url(#yantraStacked)" strokeWidth="6" opacity="0.7" />
            <path d="M 60 95 L 35 50 L 85 50 Z" fill="none" stroke="url(#yantraStacked)" strokeWidth="6" opacity="0.7" />
            <path d="M 60 35 L 78 65 L 42 65 Z" fill="none" stroke="url(#yantraStacked)" strokeWidth="6" opacity="0.6" />
            <path d="M 60 85 L 42 55 L 78 55 Z" fill="none" stroke="url(#yantraStacked)" strokeWidth="6" opacity="0.6" />
            
            <circle cx="60" cy="60" r="15" fill="url(#yantraStacked)" opacity="0.2" />
            <circle cx="60" cy="60" r="8" fill="white" stroke="#DC2626" strokeWidth="6" />
            <circle cx="60" cy="60" r="3" fill="url(#yantraStacked)" />
          </svg>
        </div>

        {/* Brand Name */}
        <div className="text-center">
          <div className="flex items-baseline justify-center gap-2">
            <span className="text-red-700 font-serif text-5xl tracking-wide">MOVA</span>
            <span className="text-gray-600 font-serif text-5xl tracking-wide">ASTRO</span>
          </div>
          
          {/* Tagline with decorative lines */}
          <div className="flex items-center justify-center gap-3 mt-3">
            <div className="w-16 h-px bg-gradient-to-r from-transparent via-gray-400 to-gray-500"></div>
            <span className="text-gray-600 text-lg font-serif tracking-wide">Reimagine Astrology</span>
            <div className="w-16 h-px bg-gradient-to-l from-transparent via-gray-400 to-gray-500"></div>
          </div>
        </div>
      </div>
    );
  }

  // Horizontal variant (default)
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {/* Sri Yantra Logo Icon */}
      <div className="relative w-14 h-14 flex-shrink-0">
        <svg viewBox="0 0 120 120" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            {/* Sri Yantra Gradient - Red to Orange to Gold */}
            <linearGradient id="yantraHoriz" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#DC2626" />
              <stop offset="50%" stopColor="#F97316" />
              <stop offset="100%" stopColor="#FBBF24" />
            </linearGradient>
          </defs>
          
          {/* Outer circle - gray */}
          <circle cx="60" cy="60" r="54" fill="none" stroke="#D1D5DB" strokeWidth="6" />
          
          {/* Inner circle ring */}
          <circle cx="60" cy="60" r="48" fill="none" stroke="url(#yantraHoriz)" strokeWidth="4" opacity="0.3" />
          
          {/* Interlocking Triangles - Sri Yantra Sacred Geometry */}
          
          {/* Upward pointing triangle (large) - Shiva/masculine */}
          <path 
            d="M 60 25 L 85 70 L 35 70 Z" 
            fill="none" 
            stroke="url(#yantraHoriz)" 
            strokeWidth="6" 
            opacity="0.7" 
          />
          
          {/* Downward pointing triangle (large) - Shakti/feminine */}
          <path 
            d="M 60 95 L 35 50 L 85 50 Z" 
            fill="none" 
            stroke="url(#yantraHoriz)" 
            strokeWidth="6" 
            opacity="0.7" 
          />
          
          {/* Upward pointing triangle (medium) */}
          <path 
            d="M 60 35 L 78 65 L 42 65 Z" 
            fill="none" 
            stroke="url(#yantraHoriz)" 
            strokeWidth="6" 
            opacity="0.6" 
          />
          
          {/* Downward pointing triangle (medium) */}
          <path 
            d="M 60 85 L 42 55 L 78 55 Z" 
            fill="none" 
            stroke="url(#yantraHoriz)" 
            strokeWidth="6" 
            opacity="0.6" 
          />
          
          {/* Center bindu area */}
          <circle cx="60" cy="60" r="15" fill="url(#yantraHoriz)" opacity="0.2" />
          
          {/* Central bindu (dot) - the source point */}
          <circle cx="60" cy="60" r="8" fill="white" stroke="#DC2626" strokeWidth="6" />
          <circle cx="60" cy="60" r="3" fill="url(#yantraHoriz)" />
        </svg>
      </div>

      {/* Text */}
      <div className="flex flex-col">
        <div className="flex items-baseline gap-1.5">
          <span className="text-red-700 font-serif text-2xl tracking-wide">MOVA</span>
          <span className="text-gray-600 font-serif text-2xl tracking-wide">ASTRO</span>
        </div>
        <div className="text-center -mt-1">
          <span className="text-gray-600 text-xs font-serif tracking-wider">Reimagine Astrology</span>
        </div>
      </div>
    </div>
  );
}